import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Globe, ShieldCheck, Folder, Bank, FileText, Books, ChartBar,
  Check, X, CaretDown, ArrowsClockwise, ArrowLeft, ListChecks,
  PencilSimple, CloudArrowUp, Eye, Gauge, Clock, CheckCircle,
  FilePdf, Database, Warning, Trash
} from '@phosphor-icons/react';
import { API, useAuth } from '@/App';

// Color and Icon mappings (same as tenant page)
const COLOR_MAP = {
  gold: { bg: 'bg-gold/10', border: 'border-gold/30', text: 'text-gold', activeBorder: 'border-gold' },
  blue: { bg: 'bg-blue-500/10', border: 'border-blue-500/30', text: 'text-blue-400', activeBorder: 'border-blue-400' },
  emerald: { bg: 'bg-emerald-500/10', border: 'border-emerald-500/30', text: 'text-emerald-400', activeBorder: 'border-emerald-400' },
  purple: { bg: 'bg-purple-500/10', border: 'border-purple-500/30', text: 'text-purple-400', activeBorder: 'border-purple-400' },
  red: { bg: 'bg-red-500/10', border: 'border-red-500/30', text: 'text-red-400', activeBorder: 'border-red-400' },
  cyan: { bg: 'bg-cyan-500/10', border: 'border-cyan-500/30', text: 'text-cyan-400', activeBorder: 'border-cyan-400' },
  amber: { bg: 'bg-amber-500/10', border: 'border-amber-500/30', text: 'text-amber-400', activeBorder: 'border-amber-400' }
};

const ICON_MAP = {
  ShieldCheck, Globe, Bank, FileText, Books, Folder, Database
};

const DEFAULT_THEMES = [
  { id: 'aml_cft_pf', name: 'AML/CFT/PF', icon: 'ShieldCheck', color: 'gold' },
  { id: 'cybersecurity', name: 'Cybersecurity', icon: 'Globe', color: 'blue' },
  { id: 'consumer_protection', name: 'Consumer Protection', icon: 'Bank', color: 'emerald' },
  { id: 'conduct', name: 'Conduct', icon: 'FileText', color: 'purple' },
  { id: 'data_privacy', name: 'Data Privacy', icon: 'Database', color: 'cyan' }
];

// Status Badge Components
const ReviewStatusBadge = ({ status }) => {
  const statusConfig = {
    draft: { bg: 'bg-gray-500/10', text: 'text-gray-400', label: 'Draft' },
    ai_extracted: { bg: 'bg-purple-500/10', text: 'text-purple-400', label: 'AI Extracted' },
    under_review: { bg: 'bg-amber-500/10', text: 'text-amber-400', label: 'Under Review' },
    approved: { bg: 'bg-emerald-500/10', text: 'text-emerald-400', label: 'Admin Approved' },
    rejected: { bg: 'bg-red-500/10', text: 'text-red-400', label: 'Rejected' }
  };
  const config = statusConfig[status] || statusConfig.ai_extracted;
  return (
    <span className={`px-2 py-0.5 text-xs rounded ${config.bg} ${config.text}`}>
      {config.label}
    </span>
  );
};

const PublishedBadge = ({ published }) => {
  return published ? (
    <span className="px-2 py-0.5 text-xs rounded bg-cyan-500/10 text-cyan-400 flex items-center gap-1">
      <CloudArrowUp size={12} />
      Published
    </span>
  ) : (
    <span className="px-2 py-0.5 text-xs rounded bg-gray-500/10 text-gray-400">
      Not Published
    </span>
  );
};

const PriorityBadge = ({ priority }) => {
  const priorityConfig = {
    critical: { bg: 'bg-red-500/10', text: 'text-red-400', label: 'Critical' },
    high: { bg: 'bg-orange-500/10', text: 'text-orange-400', label: 'High' },
    medium: { bg: 'bg-amber-500/10', text: 'text-amber-400', label: 'Medium' },
    low: { bg: 'bg-blue-500/10', text: 'text-blue-400', label: 'Low' }
  };
  const config = priorityConfig[priority] || priorityConfig.medium;
  return (
    <span className={`px-2 py-0.5 text-xs rounded ${config.bg} ${config.text}`}>
      {config.label}
    </span>
  );
};

// Edit Obligation Modal
const EditObligationModal = ({ obligation, onSave, onClose, isLoading }) => {
  const [form, setForm] = useState({
    statement: obligation.statement || '',
    statement_summary: obligation.statement_summary || '',
    regulated_party: obligation.regulated_party || '',
    action_required: obligation.action_required || '',
    priority: obligation.priority || 'medium',
    timeframe: obligation.timeframe || '',
    penalties: obligation.penalties || '',
    review_notes: obligation.review_notes || ''
  });

  const handleSubmit = () => {
    onSave(obligation.id, form);
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-surface border border-border rounded-xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <h3 className="text-lg font-semibold text-text-primary mb-4 flex items-center gap-2">
          <PencilSimple size={20} className="text-gold" />
          Edit Obligation
        </h3>

        <div className="space-y-4">
          <div>
            <label className="text-sm text-text-secondary mb-1 block">Statement</label>
            <textarea
              value={form.statement}
              onChange={e => setForm({ ...form, statement: e.target.value })}
              className="w-full p-3 bg-background border border-border rounded-lg text-sm text-text-primary focus:border-gold focus:outline-none resize-none"
              rows={4}
            />
          </div>

          <div>
            <label className="text-sm text-text-secondary mb-1 block">Summary</label>
            <input
              type="text"
              value={form.statement_summary}
              onChange={e => setForm({ ...form, statement_summary: e.target.value })}
              className="w-full p-3 bg-background border border-border rounded-lg text-sm text-text-primary focus:border-gold focus:outline-none"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-text-secondary mb-1 block">Regulated Party</label>
              <input
                type="text"
                value={form.regulated_party}
                onChange={e => setForm({ ...form, regulated_party: e.target.value })}
                className="w-full p-3 bg-background border border-border rounded-lg text-sm text-text-primary focus:border-gold focus:outline-none"
              />
            </div>
            <div>
              <label className="text-sm text-text-secondary mb-1 block">Priority</label>
              <select
                value={form.priority}
                onChange={e => setForm({ ...form, priority: e.target.value })}
                className="w-full p-3 bg-background border border-border rounded-lg text-sm text-text-primary focus:border-gold focus:outline-none"
              >
                <option value="critical">Critical</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
            </div>
          </div>

          <div>
            <label className="text-sm text-text-secondary mb-1 block">Action Required</label>
            <textarea
              value={form.action_required}
              onChange={e => setForm({ ...form, action_required: e.target.value })}
              className="w-full p-3 bg-background border border-border rounded-lg text-sm text-text-primary focus:border-gold focus:outline-none resize-none"
              rows={2}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-text-secondary mb-1 block">Timeframe</label>
              <input
                type="text"
                value={form.timeframe}
                onChange={e => setForm({ ...form, timeframe: e.target.value })}
                className="w-full p-3 bg-background border border-border rounded-lg text-sm text-text-primary focus:border-gold focus:outline-none"
              />
            </div>
            <div>
              <label className="text-sm text-text-secondary mb-1 block">Penalties</label>
              <input
                type="text"
                value={form.penalties}
                onChange={e => setForm({ ...form, penalties: e.target.value })}
                className="w-full p-3 bg-background border border-border rounded-lg text-sm text-text-primary focus:border-gold focus:outline-none"
              />
            </div>
          </div>

          <div>
            <label className="text-sm text-text-secondary mb-1 block">Review Notes</label>
            <textarea
              value={form.review_notes}
              onChange={e => setForm({ ...form, review_notes: e.target.value })}
              className="w-full p-3 bg-background border border-border rounded-lg text-sm text-text-primary focus:border-gold focus:outline-none resize-none"
              rows={2}
              placeholder="Notes about your review..."
            />
          </div>
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <button onClick={onClose} className="px-4 py-2 text-sm text-text-secondary hover:text-text-primary">
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isLoading}
            className="px-4 py-2 bg-gold/10 text-gold rounded-lg text-sm font-medium hover:bg-gold/20 flex items-center gap-2 disabled:opacity-50"
          >
            {isLoading ? <ArrowsClockwise size={14} className="animate-spin" /> : <Check size={14} />}
            Save Changes
          </button>
        </div>
      </motion.div>
    </div>
  );
};

// Admin Obligation Card with Edit, Approve, Reject, Publish actions
const AdminObligationCard = ({ obligation, onApprove, onReject, onEdit, onPublish, onUnpublish, isProcessing, isSelected, onSelect, showCheckbox }) => {
  const [showDetails, setShowDetails] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(false);

  const confidencePercent = obligation.confidence_score ? Math.round(obligation.confidence_score * 100) : null;
  const isApproved = obligation.review_status === 'approved';
  const isRejected = obligation.review_status === 'rejected';
  const isPending = ['ai_extracted', 'under_review'].includes(obligation.review_status);

  return (
    <div className={`bg-background border rounded-lg p-4 transition-all ${
      isSelected ? 'border-gold ring-1 ring-gold/30' : isRejected ? 'border-red-500/30 opacity-70' : isApproved ? 'border-emerald-500/30' : 'border-gold/30 hover:border-gold/50'
    }`}>
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          {showCheckbox && isPending && (
            <label className="flex items-center cursor-pointer mt-1">
              <input
                type="checkbox"
                checked={isSelected}
                onChange={() => onSelect(obligation.id)}
                className="w-4 h-4 rounded border-border bg-background text-gold focus:ring-gold cursor-pointer"
              />
            </label>
          )}

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <ReviewStatusBadge status={obligation.review_status} />
              <PublishedBadge published={obligation.published_to_tenants} />
              <PriorityBadge priority={obligation.priority} />
              {confidencePercent && (
                <span className="text-xs text-text-tertiary flex items-center gap-1">
                  <Gauge size={12} />
                  {confidencePercent}%
                </span>
              )}
            </div>

            <p className="text-sm text-text-primary mb-2">
              {obligation.statement_summary || obligation.statement}
            </p>

            {obligation.section_reference && (
              <p className="text-xs text-gold flex items-center gap-1">
                <ListChecks size={12} />
                {obligation.section_reference}
                {obligation.section_title && ` - ${obligation.section_title}`}
              </p>
            )}
          </div>
        </div>

        {/* Actions - Always show Edit */}
        <div className="flex items-center gap-2 flex-shrink-0 flex-wrap justify-end">
          <button
            onClick={() => onEdit(obligation)}
            className="px-2 py-1.5 bg-blue-500/10 text-blue-400 rounded text-xs font-medium hover:bg-blue-500/20 flex items-center gap-1"
            data-testid={`edit-obligation-${obligation.id}`}
          >
            <PencilSimple size={14} />
            Edit
          </button>

          {isPending && !showCheckbox && (
            <>
              <button
                onClick={() => onApprove(obligation.id)}
                disabled={isProcessing}
                className="px-2 py-1.5 bg-emerald-500/10 text-emerald-400 rounded text-xs font-medium hover:bg-emerald-500/20 flex items-center gap-1 disabled:opacity-50"
                data-testid={`approve-obligation-${obligation.id}`}
              >
                <Check size={14} />
                Approve
              </button>
              <button
                onClick={() => setShowRejectModal(true)}
                disabled={isProcessing}
                className="px-2 py-1.5 bg-red-500/10 text-red-400 rounded text-xs font-medium hover:bg-red-500/20 flex items-center gap-1 disabled:opacity-50"
              >
                <X size={14} />
                Reject
              </button>
            </>
          )}

          {isApproved && !obligation.published_to_tenants && (
            <button
              onClick={() => onPublish(obligation.id)}
              disabled={isProcessing}
              className="px-2 py-1.5 bg-cyan-500/10 text-cyan-400 rounded text-xs font-medium hover:bg-cyan-500/20 flex items-center gap-1 disabled:opacity-50"
            >
              <CloudArrowUp size={14} />
              Publish
            </button>
          )}

          {obligation.published_to_tenants && (
            <button
              onClick={() => onUnpublish(obligation.id)}
              disabled={isProcessing}
              className="px-2 py-1.5 bg-gray-500/10 text-gray-400 rounded text-xs font-medium hover:bg-gray-500/20 flex items-center gap-1 disabled:opacity-50"
            >
              <X size={14} />
              Unpublish
            </button>
          )}
        </div>
      </div>

      {/* Expandable Details */}
      <button
        onClick={() => setShowDetails(!showDetails)}
        className="mt-3 text-xs text-text-secondary hover:text-gold flex items-center gap-1"
      >
        <CaretDown size={12} className={`transition-transform ${showDetails ? 'rotate-180' : ''}`} />
        {showDetails ? 'Hide details' : 'Show details'}
      </button>

      {showDetails && (
        <div className="mt-3 pt-3 border-t border-border space-y-2 text-xs">
          {obligation.regulated_party && (
            <div><span className="text-text-tertiary">Regulated Party: </span><span className="text-text-secondary">{obligation.regulated_party}</span></div>
          )}
          {obligation.action_required && (
            <div><span className="text-text-tertiary">Action Required: </span><span className="text-text-secondary">{obligation.action_required}</span></div>
          )}
          {obligation.timeframe && (
            <div><span className="text-text-tertiary">Timeframe: </span><span className="text-text-secondary">{obligation.timeframe}</span></div>
          )}
          {obligation.penalties && (
            <div><span className="text-text-tertiary">Penalties: </span><span className="text-text-secondary">{obligation.penalties}</span></div>
          )}
          {obligation.reviewed_by && (
            <div><span className="text-text-tertiary">Reviewed by: </span><span className="text-text-secondary">{obligation.reviewed_by}</span></div>
          )}
          {obligation.review_notes && (
            <div className="mt-2 p-2 bg-surface rounded">
              <span className="text-text-tertiary">Review Notes: </span><span className="text-text-secondary">{obligation.review_notes}</span>
            </div>
          )}
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowRejectModal(false)}>
          <div className="bg-surface border border-border rounded-xl p-6 max-w-md w-full" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-text-primary mb-4">Reject Obligation</h3>
            <textarea
              value={rejectReason}
              onChange={e => setRejectReason(e.target.value)}
              placeholder="Enter rejection reason..."
              className="w-full p-3 bg-background border border-border rounded-lg text-sm focus:border-gold focus:outline-none resize-none"
              rows={3}
            />
            <div className="flex justify-end gap-3 mt-4">
              <button onClick={() => setShowRejectModal(false)} className="px-4 py-2 text-sm text-text-secondary">Cancel</button>
              <button
                onClick={() => { onReject(obligation.id, rejectReason); setShowRejectModal(false); setRejectReason(''); }}
                className="px-4 py-2 bg-red-500/10 text-red-400 rounded-lg text-sm font-medium hover:bg-red-500/20"
              >
                Confirm Reject
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Law Section for Admin
const AdminLawObligationsSection = ({ law, onApprove, onReject, onEdit, onPublish, onUnpublish, onReparse, isProcessing, defaultExpanded = false, selectedIds, onSelectObligation, onSelectAllInLaw, bulkMode }) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [showReparseConfirm, setShowReparseConfirm] = useState(false);

  const pendingObligations = law.obligations.filter(o => ['ai_extracted', 'under_review'].includes(o.review_status));
  const pendingCount = pendingObligations.length;
  const approvedCount = law.obligations.filter(o => o.review_status === 'approved').length;
  const publishedCount = law.obligations.filter(o => o.published_to_tenants).length;
  const rejectedCount = law.obligations.filter(o => o.review_status === 'rejected').length;

  const allPendingSelected = pendingObligations.length > 0 && pendingObligations.every(o => selectedIds.includes(o.id));
  const somePendingSelected = pendingObligations.some(o => selectedIds.includes(o.id));

  return (
    <div className="bg-surface border border-border rounded-lg overflow-hidden">
      <div className="flex items-center">
        {bulkMode && pendingCount > 0 && (
          <div className="pl-4 pr-2 py-4 border-r border-border">
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={allPendingSelected}
                ref={el => { if (el) el.indeterminate = somePendingSelected && !allPendingSelected; }}
                onChange={() => onSelectAllInLaw(law.law_id, pendingObligations.map(o => o.id))}
                className="w-4 h-4 rounded border-border bg-background text-gold focus:ring-gold cursor-pointer"
              />
            </label>
          </div>
        )}

        <button onClick={() => setExpanded(!expanded)} className="flex-1 p-4 flex items-center justify-between hover:bg-background/50 transition-all">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
              <FilePdf size={20} className="text-red-400" />
            </div>
            <div className="text-left">
              <h4 className="font-medium text-text-primary">{law.law_name}</h4>
              <p className="text-xs text-text-tertiary">{law.jurisdiction} • {law.obligation_count} obligations</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 text-xs">
              {pendingCount > 0 && <span className="px-2 py-1 rounded bg-purple-500/10 text-purple-400">{pendingCount} pending</span>}
              {approvedCount > 0 && <span className="px-2 py-1 rounded bg-emerald-500/10 text-emerald-400">{approvedCount} approved</span>}
              {publishedCount > 0 && <span className="px-2 py-1 rounded bg-cyan-500/10 text-cyan-400">{publishedCount} published</span>}
              {rejectedCount > 0 && <span className="px-2 py-1 rounded bg-red-500/10 text-red-400">{rejectedCount} rejected</span>}
            </div>
            <CaretDown size={20} className={`text-text-tertiary transition-transform ${expanded ? 'rotate-180' : ''}`} />
          </div>
        </button>
      </div>

      {expanded && (
        <div className="border-t border-border p-4 space-y-3">
          {/* Re-parse Action Bar */}
          <div className="flex items-center justify-between p-3 bg-background rounded-lg border border-border">
            <div className="text-sm text-text-secondary">
              <span className="text-text-primary font-medium">{law.obligation_count}</span> obligations extracted from this document
            </div>
            <button
              onClick={() => setShowReparseConfirm(true)}
              disabled={isProcessing}
              className="px-3 py-1.5 bg-amber-500/10 text-amber-400 rounded-lg text-xs font-medium hover:bg-amber-500/20 transition-all flex items-center gap-2 disabled:opacity-50"
              data-testid={`reparse-law-${law.law_id}`}
            >
              <ArrowsClockwise size={14} />
              Re-parse Document
            </button>
          </div>

          {law.obligations.length > 0 ? (
            law.obligations.map(ob => (
              <AdminObligationCard
                key={ob.id}
                obligation={ob}
                onApprove={onApprove}
                onReject={onReject}
                onEdit={onEdit}
                onPublish={onPublish}
                onUnpublish={onUnpublish}
                isProcessing={isProcessing}
                isSelected={selectedIds.includes(ob.id)}
                onSelect={onSelectObligation}
                showCheckbox={bulkMode}
              />
            ))
          ) : (
            <p className="text-sm text-text-tertiary text-center py-4">No obligations extracted from this document yet</p>
          )}
        </div>
      )}

      {/* Re-parse Confirmation Modal */}
      {showReparseConfirm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowReparseConfirm(false)}>
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-surface border border-border rounded-xl p-6 max-w-md w-full"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-amber-500/10 rounded-lg flex items-center justify-center">
                <Warning size={24} className="text-amber-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-text-primary">Re-parse Document</h3>
                <p className="text-sm text-text-secondary">This will regenerate all obligations</p>
              </div>
            </div>

            <div className="bg-background rounded-lg p-4 mb-4 text-sm">
              <p className="text-text-secondary mb-2">This action will:</p>
              <ul className="space-y-1 text-text-tertiary">
                <li>• Clear all <span className="text-amber-400 font-medium">{law.obligation_count}</span> existing obligations</li>
                <li>• Re-extract obligations with updated NAVI logic</li>
                <li>• Apply new filtering rules (regulated entity only)</li>
                <li>• All new obligations will need fresh review</li>
              </ul>
            </div>

            <div className="flex justify-end gap-3">
              <button onClick={() => setShowReparseConfirm(false)} className="px-4 py-2 text-sm text-text-secondary hover:text-text-primary">
                Cancel
              </button>
              <button
                onClick={() => { onReparse(law.law_id); setShowReparseConfirm(false); }}
                disabled={isProcessing}
                className="px-4 py-2 bg-amber-500/20 text-amber-400 rounded-lg text-sm font-medium hover:bg-amber-500/30 flex items-center gap-2 disabled:opacity-50"
              >
                <ArrowsClockwise size={14} />
                Confirm Re-parse
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

// Admin Theme Detail Panel
const AdminThemeDetailPanel = ({ theme, onClose }) => {
  const [activeTab, setActiveTab] = useState('obligations');
  const [loading, setLoading] = useState(true);
  const [lawsWithObligations, setLawsWithObligations] = useState([]);
  const [stats, setStats] = useState({ total_obligations: 0, pending_review: 0, approved: 0, rejected: 0, published: 0 });
  const [filter, setFilter] = useState('all');
  const [processingId, setProcessingId] = useState(null);
  const [bulkMode, setBulkMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkProcessing, setBulkProcessing] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkNotes, setBulkNotes] = useState('');
  const [bulkAutoPublish, setBulkAutoPublish] = useState(true);
  const [editingObligation, setEditingObligation] = useState(null);

  const colors = COLOR_MAP[theme.color] || COLOR_MAP.gold;
  const ThemeIcon = ICON_MAP[theme.icon] || Folder;

  useEffect(() => {
    fetchObligations();
  }, [theme.id, filter]);

  useEffect(() => {
    setSelectedIds([]);
  }, [theme.id, filter]);

  const fetchObligations = async () => {
    setLoading(true);
    try {
      const statusParam = filter !== 'all' ? `?status=${filter === 'pending' ? 'ai_extracted' : filter}` : '';
      const response = await axios.get(`${API}/admin/regulatory-universe/themes/${theme.id}/obligations-grouped${statusParam}`);
      setLawsWithObligations(response.data.laws || []);
      setStats(response.data.stats || {});
    } catch (error) {
      console.error('Failed to load obligations:', error);
      toast.error('Failed to load obligations');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (obligationId) => {
    setProcessingId(obligationId);
    try {
      await axios.post(`${API}/admin/regulatory-universe/obligations/${obligationId}/approve`);
      toast.success('Obligation approved');
      fetchObligations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve');
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async (obligationId, reason) => {
    setProcessingId(obligationId);
    try {
      await axios.post(`${API}/admin/regulatory-universe/obligations/${obligationId}/reject?review_notes=${encodeURIComponent(reason || '')}`);
      toast.success('Obligation rejected');
      fetchObligations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject');
    } finally {
      setProcessingId(null);
    }
  };

  const handleEdit = (obligation) => {
    setEditingObligation(obligation);
  };

  const handleSaveEdit = async (obligationId, formData) => {
    try {
      await axios.put(`${API}/admin/regulatory-universe/obligations/${obligationId}`, formData);
      toast.success('Obligation updated');
      setEditingObligation(null);
      fetchObligations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update');
    }
  };

  const handlePublish = async (obligationId) => {
    setProcessingId(obligationId);
    try {
      await axios.post(`${API}/admin/regulatory-universe/obligations/publish`, { obligation_ids: [obligationId] });
      toast.success('Published to tenants');
      fetchObligations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to publish');
    } finally {
      setProcessingId(null);
    }
  };

  const handleUnpublish = async (obligationId) => {
    setProcessingId(obligationId);
    try {
      await axios.post(`${API}/admin/regulatory-universe/obligations/${obligationId}/unpublish`);
      toast.success('Unpublished from tenants');
      fetchObligations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unpublish');
    } finally {
      setProcessingId(null);
    }
  };

  const handleSelectObligation = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const handleSelectAllInLaw = (lawId, obligationIds) => {
    const allSelected = obligationIds.every(id => selectedIds.includes(id));
    if (allSelected) {
      setSelectedIds(prev => prev.filter(id => !obligationIds.includes(id)));
    } else {
      setSelectedIds(prev => [...new Set([...prev, ...obligationIds])]);
    }
  };

  const handleBulkApprove = async () => {
    if (selectedIds.length === 0) return;
    setBulkProcessing(true);
    try {
      const response = await axios.post(`${API}/admin/regulatory-universe/obligations/bulk-approve`, {
        obligation_ids: selectedIds,
        review_notes: bulkNotes || 'Bulk approved by admin',
        auto_publish: bulkAutoPublish
      });
      toast.success(`Approved ${response.data.success_count} obligations${bulkAutoPublish ? ' and published to tenants' : ''}`);
      setSelectedIds([]);
      setBulkNotes('');
      setShowBulkModal(false);
      setBulkMode(false);
      fetchObligations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to bulk approve');
    } finally {
      setBulkProcessing(false);
    }
  };

  const handleReparse = async (lawId) => {
    setProcessingId(lawId);
    try {
      toast.info('Re-parsing document with updated NAVI logic...', { duration: 10000 });
      const response = await axios.post(`${API}/admin/regulatory-universe/laws/${lawId}/reparse`);
      toast.dismiss();
      toast.success(
        `Re-parsed successfully! ${response.data.previous.obligations} → ${response.data.current.obligations} obligations`,
        { duration: 5000 }
      );
      fetchObligations();
    } catch (error) {
      toast.dismiss();
      toast.error(error.response?.data?.detail || 'Failed to re-parse document');
    } finally {
      setProcessingId(null);
    }
  };

  const allPendingIds = lawsWithObligations.flatMap(law => 
    law.obligations.filter(o => ['ai_extracted', 'under_review'].includes(o.review_status)).map(o => o.id)
  );

  const filterTabs = [
    { id: 'all', label: 'All', count: stats.total_obligations },
    { id: 'pending', label: 'Pending', count: stats.pending_review },
    { id: 'approved', label: 'Approved', count: stats.approved },
    { id: 'rejected', label: 'Rejected', count: stats.rejected }
  ];

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      className="flex-1 bg-surface border border-border rounded-xl overflow-hidden"
    >
      {/* Header */}
      <div className={`p-6 ${colors.bg} border-b border-border`}>
        <button onClick={onClose} className="flex items-center gap-2 text-text-secondary hover:text-gold mb-4">
          <ArrowLeft size={16} /> Back to Themes
        </button>
        <div className="flex items-center gap-4">
          <div className={`w-14 h-14 ${colors.bg} border ${colors.border} rounded-xl flex items-center justify-center`}>
            <ThemeIcon size={28} className={colors.text} />
          </div>
          <div>
            <h2 className="text-xl font-bold text-text-primary">{theme.name}</h2>
            <p className="text-sm text-text-secondary">Admin Review Portal</p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6 space-y-6">
        {/* Header Actions */}
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-text-primary">Obligations Review Queue</h3>
            <p className="text-sm text-text-secondary">Review, edit, and publish obligations to tenants</p>
          </div>
          <div className="flex items-center gap-2">
            {stats.pending_review > 0 && (
              <button
                onClick={() => { setBulkMode(!bulkMode); if (bulkMode) setSelectedIds([]); }}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium flex items-center gap-2 ${
                  bulkMode ? 'bg-gold/10 text-gold border border-gold' : 'bg-surface border border-border text-text-secondary hover:border-gold hover:text-gold'
                }`}
                data-testid="bulk-mode-toggle"
              >
                <ListChecks size={14} />
                {bulkMode ? 'Exit Bulk Mode' : 'Bulk Select'}
              </button>
            )}
            <button onClick={fetchObligations} className="px-3 py-1.5 bg-surface border border-border rounded-lg text-sm text-text-secondary hover:border-gold hover:text-gold flex items-center gap-2">
              <ArrowsClockwise size={14} className={loading ? 'animate-spin' : ''} />
              Refresh
            </button>
          </div>
        </div>

        {/* Bulk Action Bar */}
        {bulkMode && selectedIds.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="sticky top-0 z-20 bg-gold/10 border border-gold/30 rounded-lg p-4 flex items-center justify-between"
          >
            <div className="flex items-center gap-4">
              <span className="text-sm text-gold font-medium">{selectedIds.length} selected</span>
              <button onClick={() => setSelectedIds([])} className="text-xs text-text-secondary hover:text-gold">Clear</button>
            </div>
            <button
              onClick={() => setShowBulkModal(true)}
              className="px-4 py-2 bg-emerald-500/20 text-emerald-400 rounded-lg text-sm font-medium hover:bg-emerald-500/30 flex items-center gap-2"
              data-testid="bulk-approve-button"
            >
              <Check size={16} />
              Approve & Publish {selectedIds.length}
            </button>
          </motion.div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-5 gap-4">
          <div className="p-4 bg-background border border-border rounded-lg">
            <div className="flex items-center gap-2 mb-1">
              <FileText size={18} className="text-blue-400" />
              <span className="text-2xl font-mono font-bold text-text-primary">{stats.total_obligations}</span>
            </div>
            <p className="text-xs text-text-tertiary">Total</p>
          </div>
          <div className="p-4 bg-purple-500/5 border border-purple-500/20 rounded-lg">
            <div className="flex items-center gap-2 mb-1">
              <Clock size={18} className="text-purple-400" />
              <span className="text-2xl font-mono font-bold text-purple-400">{stats.pending_review}</span>
            </div>
            <p className="text-xs text-text-tertiary">Pending</p>
          </div>
          <div className="p-4 bg-emerald-500/5 border border-emerald-500/20 rounded-lg">
            <div className="flex items-center gap-2 mb-1">
              <CheckCircle size={18} className="text-emerald-400" />
              <span className="text-2xl font-mono font-bold text-emerald-400">{stats.approved}</span>
            </div>
            <p className="text-xs text-text-tertiary">Approved</p>
          </div>
          <div className="p-4 bg-cyan-500/5 border border-cyan-500/20 rounded-lg">
            <div className="flex items-center gap-2 mb-1">
              <CloudArrowUp size={18} className="text-cyan-400" />
              <span className="text-2xl font-mono font-bold text-cyan-400">{stats.published}</span>
            </div>
            <p className="text-xs text-text-tertiary">Published</p>
          </div>
          <div className="p-4 bg-red-500/5 border border-red-500/20 rounded-lg">
            <div className="flex items-center gap-2 mb-1">
              <X size={18} className="text-red-400" />
              <span className="text-2xl font-mono font-bold text-red-400">{stats.rejected}</span>
            </div>
            <p className="text-xs text-text-tertiary">Rejected</p>
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="flex items-center gap-2 border-b border-border pb-2">
          {filterTabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setFilter(tab.id)}
              className={`px-4 py-2 text-sm font-medium rounded-t-lg flex items-center gap-2 ${
                filter === tab.id
                  ? 'bg-gold/10 text-gold border-b-2 border-gold -mb-[2px]'
                  : 'text-text-secondary hover:text-text-primary hover:bg-surface/50'
              }`}
            >
              {tab.label}
              <span className={`px-1.5 py-0.5 text-xs rounded ${filter === tab.id ? 'bg-gold/20' : 'bg-background'}`}>{tab.count}</span>
            </button>
          ))}
        </div>

        {/* Laws with Obligations */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <ArrowsClockwise size={32} className="animate-spin text-gold" />
          </div>
        ) : lawsWithObligations.length > 0 ? (
          <div className="space-y-4">
            {lawsWithObligations.map((law, idx) => (
              <AdminLawObligationsSection
                key={law.law_id}
                law={law}
                onApprove={handleApprove}
                onReject={handleReject}
                onEdit={handleEdit}
                onPublish={handlePublish}
                onUnpublish={handleUnpublish}
                onReparse={handleReparse}
                isProcessing={processingId !== null || bulkProcessing}
                defaultExpanded={idx === 0}
                selectedIds={selectedIds}
                onSelectObligation={handleSelectObligation}
                onSelectAllInLaw={handleSelectAllInLaw}
                bulkMode={bulkMode}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-16 bg-background border border-border rounded-lg">
            <FileText size={48} className="mx-auto text-text-tertiary mb-4 opacity-50" />
            <p className="text-text-secondary mb-2">{filter === 'all' ? 'No obligations extracted yet' : `No ${filter} obligations`}</p>
          </div>
        )}
      </div>

      {/* Bulk Approve Modal */}
      {showBulkModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowBulkModal(false)}>
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-surface border border-border rounded-xl p-6 max-w-md w-full"
            onClick={e => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold text-text-primary mb-2">Bulk Approve Obligations</h3>
            <p className="text-sm text-text-secondary mb-4">
              Approving <span className="text-gold font-semibold">{selectedIds.length}</span> obligations
            </p>

            <div className="space-y-4">
              <div>
                <label className="text-sm text-text-secondary mb-1 block">Review Notes</label>
                <textarea
                  value={bulkNotes}
                  onChange={e => setBulkNotes(e.target.value)}
                  placeholder="Add notes..."
                  className="w-full p-3 bg-background border border-border rounded-lg text-sm focus:border-gold focus:outline-none resize-none"
                  rows={2}
                />
              </div>

              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={bulkAutoPublish}
                  onChange={e => setBulkAutoPublish(e.target.checked)}
                  className="w-4 h-4 rounded border-border bg-background text-gold focus:ring-gold cursor-pointer"
                />
                <span className="text-sm text-text-primary">Publish to tenants immediately</span>
              </label>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setShowBulkModal(false)} className="px-4 py-2 text-sm text-text-secondary">Cancel</button>
              <button
                onClick={handleBulkApprove}
                disabled={bulkProcessing}
                className="px-4 py-2 bg-emerald-500/20 text-emerald-400 rounded-lg text-sm font-medium hover:bg-emerald-500/30 flex items-center gap-2 disabled:opacity-50"
              >
                {bulkProcessing ? <ArrowsClockwise size={14} className="animate-spin" /> : <Check size={14} />}
                Approve {selectedIds.length}
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Edit Obligation Modal */}
      {editingObligation && (
        <EditObligationModal
          obligation={editingObligation}
          onSave={handleSaveEdit}
          onClose={() => setEditingObligation(null)}
          isLoading={processingId !== null}
        />
      )}
    </motion.div>
  );
};

// Main Admin Regulatory Universe Page
const AdminRegulatoryUniversePage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [selectedTheme, setSelectedTheme] = useState(null);
  const [themes, setThemes] = useState(DEFAULT_THEMES);

  useEffect(() => {
    if (!user?.is_navigate_admin) {
      toast.error('Admin access required');
      navigate('/dashboard');
      return;
    }
    fetchData();
    fetchThemes();
  }, [user, navigate]);

  const fetchData = async () => {
    try {
      const response = await axios.get(`${API}/admin/regulatory-universe/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to load admin stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchThemes = async () => {
    try {
      const response = await axios.get(`${API}/regulatory-themes`);
      if (response.data.themes) {
        const converted = response.data.themes.map(t => ({
          id: t.id,
          name: t.label,
          icon: t.icon || 'Folder',
          color: t.color ? 'gold' : 'gold'
        }));
        setThemes(converted);
      }
    } catch (error) {
      console.error('Failed to load themes:', error);
    }
  };

  const getIconComponent = (iconName) => ICON_MAP[iconName] || Folder;

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <ArrowsClockwise size={32} className="animate-spin text-gold" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-[1600px] mx-auto p-6">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-2 text-text-tertiary text-sm mb-4">
            <Link to="/admin" className="hover:text-gold">Admin</Link>
            <span>/</span>
            <span className="text-gold">Regulatory Universe</span>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-text-primary flex items-center gap-3">
                <Globe size={28} className="text-gold" />
                Admin Regulatory Universe
              </h1>
              <p className="text-text-secondary mt-1">
                Review and publish regulatory obligations to tenants
              </p>
            </div>
          </div>
        </div>

        {/* Stats Overview */}
        {stats && (
          <div className="grid grid-cols-4 gap-4 mb-8">
            <div className="p-6 bg-surface border border-border rounded-xl">
              <div className="flex items-center gap-3 mb-2">
                <FileText size={24} className="text-blue-400" />
                <span className="text-3xl font-mono font-bold text-text-primary">{stats.obligations.total}</span>
              </div>
              <p className="text-sm text-text-secondary">Total Obligations</p>
            </div>
            <div className="p-6 bg-purple-500/5 border border-purple-500/20 rounded-xl">
              <div className="flex items-center gap-3 mb-2">
                <Clock size={24} className="text-purple-400" />
                <span className="text-3xl font-mono font-bold text-purple-400">{stats.obligations.pending_review}</span>
              </div>
              <p className="text-sm text-text-secondary">Pending Review</p>
            </div>
            <div className="p-6 bg-emerald-500/5 border border-emerald-500/20 rounded-xl">
              <div className="flex items-center gap-3 mb-2">
                <CheckCircle size={24} className="text-emerald-400" />
                <span className="text-3xl font-mono font-bold text-emerald-400">{stats.obligations.approved}</span>
              </div>
              <p className="text-sm text-text-secondary">Approved</p>
            </div>
            <div className="p-6 bg-cyan-500/5 border border-cyan-500/20 rounded-xl">
              <div className="flex items-center gap-3 mb-2">
                <CloudArrowUp size={24} className="text-cyan-400" />
                <span className="text-3xl font-mono font-bold text-cyan-400">{stats.obligations.published_to_tenants}</span>
              </div>
              <p className="text-sm text-text-secondary">Published to Tenants</p>
            </div>
          </div>
        )}

        {/* Main Content */}
        <div className="flex gap-6">
          {/* Themes List */}
          <div className="w-80 flex-shrink-0">
            <div className="bg-surface border border-border rounded-xl p-4">
              <h3 className="text-sm font-semibold text-text-primary mb-4 flex items-center gap-2">
                <Folder size={16} className="text-gold" />
                Regulatory Themes
              </h3>
              <div className="space-y-2">
                {themes.map(theme => {
                  const ThemeIcon = getIconComponent(theme.icon);
                  const colors = COLOR_MAP[theme.color] || COLOR_MAP.gold;
                  const isSelected = selectedTheme?.id === theme.id;

                  return (
                    <button
                      key={theme.id}
                      onClick={() => setSelectedTheme(theme)}
                      className={`w-full p-3 rounded-lg flex items-center gap-3 transition-all ${
                        isSelected
                          ? `${colors.bg} border ${colors.border}`
                          : 'hover:bg-background border border-transparent'
                      }`}
                    >
                      <div className={`w-8 h-8 ${colors.bg} rounded-lg flex items-center justify-center`}>
                        <ThemeIcon size={16} className={colors.text} />
                      </div>
                      <span className={`text-sm font-medium ${isSelected ? colors.text : 'text-text-primary'}`}>
                        {theme.name}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Theme Detail or Placeholder */}
          {selectedTheme ? (
            <AdminThemeDetailPanel theme={selectedTheme} onClose={() => setSelectedTheme(null)} />
          ) : (
            <div className="flex-1 bg-surface border border-border rounded-xl flex items-center justify-center">
              <div className="text-center">
                <Globe size={64} className="mx-auto text-text-tertiary mb-4 opacity-50" />
                <p className="text-text-secondary">Select a regulatory theme to review obligations</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminRegulatoryUniversePage;
